<template>
  <mf-card-container v-model="search" :title="`Ofertas ${textType}`" use-search placeholder="Buscar produto ou grupo">
    <template v-slot:headers>
      <div class="mr-2">
        <mf-button label="Editar" text color="marca-0-l40" class="unset-capitalize" @click="openUnitsDialog" />
        <mf-select-units-dialog
          ref="mfSelectUnitsDialog"
          hidden-template
          :selected-units.sync="selectedUnits"
          :units="units"
          :tag-list="tagList"
          :group-list="unitsGroups"
          hide-ecommerce-configuration
          @change="editOffers"
        />
      </div>
      <headers-data-table-settings
        :headers.sync="headers"
        :default-headers="getDefaultHeadersOptions()"
        :headers-settings-name="`mobile-offers-highlight-headers-configs-${type}`"
      />
    </template>
    <paginated-data-table
      ref="table"
      :headers="computedHeaders"
      :query="query"
      :search="search"
      query-name="highlightedSalesProducts"
      :query-variables="queryVariables"
      :disable-sort="true"
      :custom-items-per-page="5"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
    >
      <template v-slot:item.ranking="{ item, header, index }">
        <div :style="`width: ${header.width}`">
          {{ `${findPosition(item)}º` }}
        </div>
      </template>
      <template v-slot:item.image_url="{ item }">
        <div v-if="item.image_url && item.image_url.length" class="img-container d-flex align-center justify-center">
          <img :src="item.image_url" alt="" width="100%" />
        </div>
        <mf-icon v-else>image_not_supported</mf-icon>
      </template>

      <template v-slot:item.sku="{ item, header }">
        <div :style="`width: ${header.width}`">
          <span v-if="item.products">
            <v-tooltip max-width="250" right>
              <template v-slot:activator="{ on }">
                <span v-on="on">{{ item.products.length === 1 ? '1 código' : `${item.products.length} códigos` }}</span>
              </template>
              <span v-for="(product, index) in item.products" :key="index" style="display: block">
                {{ product.sku }}
              </span>
            </v-tooltip>
          </span>
          <a v-else-if="item.sku" target="_blank" class="link" :href="link(item.sku)">{{ item.sku }}</a>
          <span v-else> - </span>
        </div>
      </template>

      <template v-slot:item.name="{ item, header }">
        <div :style="`width: ${header.width}`">
          <v-tooltip top>
            <template #activator="{ on }">
              <span class="texto-corrido" v-on="on">
                {{ putEllipsis(item.name, 25) }}
              </span>
            </template>
            <span>{{ item.name }}</span>
          </v-tooltip>
        </div>
      </template>

      <template v-slot:item.personalized="{ item, header }">
        <div :style="`width: ${header.width}`">
          {{ getProductTypeLabel(item.personalized) }}
        </div>
      </template>

      <template v-slot:item.type="{ item, header }">
        <div :style="`width: ${header.width}`">
          {{ getProductFormatTypeLabel(item.type) }}
        </div>
      </template>

      <template v-slot:item.dates="{ item, header }">
        <div :style="`width: ${header.width}`" class="d-flex align-center">
          <date-time-status-colors :start-date="item.start_date" :end-date="item.end_date" />
          <div>
            {{ validityDates(item.start_date, item.end_date) }}
          </div>
        </div>
      </template>

      <template v-slot:item.cashback_absolute="{ item, header }">
        <div :style="`width: ${header.width}`">
          <span v-if="item.cashback_percentage">{{ item.cashback_percentage | localeString(0) | percent }}</span>
          <span v-else-if="item.cashback_absolute">{{ item.cashback_absolute | localeString(2) | currency }}</span>
          <span v-else>-</span>
        </div>
      </template>

      <template v-slot:item.additional_points_rate="{ item, header }">
        <div :style="`width: ${header.width}`">
          <span v-if="item.additional_points_rate">{{ item.additional_points_rate | localeString(0) }}%</span>
          <span v-else>-</span>
        </div>
      </template>

      <template v-slot:item.discount="{ item, header }">
        <div :style="`width: ${header.width}`">
          <span>{{ item.discount_percentage ? formatPercent(item.discount_percentage) : '-' }}</span>
        </div>
      </template>

      <template slot="no-data">
        <span>Nenhuma oferta {{ type === 'ativaveis' ? 'ativável' : 'livre' }} foi colocada em destaque ainda</span>
      </template>
    </paginated-data-table>
  </mf-card-container>
</template>

<script>
import { HIGHLIGHTED_SALE_PRODUCTS } from '../../graphql'
import { floatToPercentString, localeString, putEllipsis } from '@/config/helpers.js'
import { getProductTypeLabel, getProductFormatTypeLabel } from '@/modules/mobile/ofertas/helpers/productType.js'
import { DateTime } from 'luxon'
import { mapGetters } from 'vuex'
import cloneDeep from 'lodash/cloneDeep'
export default {
  components: {
    PaginatedDataTable: () => import('@/components/PaginatedDataTable.vue'),
    DateTimeStatusColors: () => import('./DateTimeStatusColors.vue'),
    HeadersDataTableSettings: () => import('@/components/HeadersDataTableSettings.vue'),
  },
  props: {
    type: {
      type: String,
      required: true,
    },
    unitSelected: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    options: {},
    page: 0,
    itemsPerPage: 0,
    selectedUnits: [],
    search: '',
    headers: [],
  }),
  computed: {
    ...mapGetters(['tagList', 'units', 'unitsGroups']),
    query() {
      return HIGHLIGHTED_SALE_PRODUCTS
    },
    queryVariables() {
      return {
        app_units: this.unitIdArray && Boolean(this.unitIdArray.length) ? this.unitIdArray : [],
        activatable: this.type === 'ativaveis',
        ...(this.isFranchisee && { franchise: this.isFranchisee }),
      }
    },
    unitIdArray() {
      return [this.unitSelected.id_erp]
    },
    textType() {
      return this.type === 'ativaveis' ? 'ativáveis' : 'livres'
    },
    isFranchise() {
      // rede
      if (!this.$getLoggedUser().is_franchise) return false
      return this.$getLoggedUser().is_franchise
    },
    isFranchisee() {
      // franquia
      if (!this.$getLoggedUser().is_franchisee) return false
      return this.$getLoggedUser().is_franchisee
    },
    hasCashback() {
      return this.$getAllowedModules()['cashback']
    },
    hasPoints() {
      return this.$getAllowedModules()['points']
    },
    computedHeaders() {
      const headers = this.headers.filter((header) => header.isActive)
      return headers.map((header) => {
        return {
          ...header,
          width: header.width,
        }
      })
    },
    defaultHeaders() {
      let headers = [
        {
          text: '',
          sortable: false,
          value: 'ranking',
          width: '25px',
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Imagem',
          value: 'image_url',
          width: '75px',
          sortable: false,
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Código',
          value: 'sku',
          width: '120px',
          sortable: false,
          isActive: true,
          isRequired: true,
          isDefault: true,
        },
        {
          text: 'Nome em exibição',
          value: 'name',
          width: '250px',
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Tipo',
          value: 'personalized',
          width: '90px',
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Formato',
          value: 'type',
          width: '130px',
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Validade',
          value: 'dates',
          width: '110px',
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        ...(this.hasCashback
          ? [
              {
                text: 'Cashback',
                value: 'cashback_absolute',
                width: '100px',
                isActive: true,
                isRequired: false,
                isDefault: true,
              },
            ]
          : []),
        ...(this.hasPoints
          ? [
              {
                text: 'Pontos extras',
                value: 'additional_points_rate',
                width: '110px',
                isActive: true,
                isRequired: false,
                isDefault: true,
              },
            ]
          : []),
        {
          text: 'Desconto',
          value: 'discount',
          width: '150px',
          sortable: false,
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
      ]
      return headers
    },
    pagePosition() {
      return this.itemsPerPage * (this.page - 1)
    },
  },
  watch: {
    unitIdArray: {
      handler(value) {
        this.selectedUnits = value
      },
      immediate: true,
    },
  },
  async mounted() {
    if (!this.headers.length) this.headers = cloneDeep(this.defaultHeaders)
  },
  methods: {
    getProductTypeLabel,
    getProductFormatTypeLabel,
    putEllipsis,
    link(sku) {
      return `#/produtos/detalhes-produto/${sku}`
    },
    openUnitsDialog() {
      this.$refs.mfSelectUnitsDialog.dialog = true
    },
    getDefaultHeadersOptions() {
      return cloneDeep(this.defaultHeaders)
    },
    formatPercent(value) {
      return floatToPercentString(value)
    },
    editOffers() {
      this.$store.dispatch(
        'selectUnits',
        this.units.filter((unit) => this.selectedUnits.includes(unit.id_erp))
      )
      this.$router.push(`/mobile/ofertas/destaques/editar/${this.type}`).catch(() => {})
      window.scrollTo(0, 0)
    },
    validityDates(startDate, endDate) {
      return `${DateTime.fromFormat(startDate, 'yyyy-MM-dd').toFormat('dd/MM/yyyy')} - ${DateTime.fromFormat(endDate, 'yyyy-MM-dd').toFormat('dd/MM/yyyy')}`
    },
    findPosition(item) {
      const allPageItems = this.$refs.table.items
      const index = allPageItems.findIndex((currentItem) => item._id === currentItem._id)

      return index + 1 + this.pagePosition
    },
    getItemDiscount(item) {
      const { type, price, discounted_price, take, pay, target_products, percentage_discount } = item

      const discounts = {
        general: !this.isFranchise
          ? `R$ ${localeString(discounted_price, 2)} / R$ ${localeString(price, 2)}`
          : price
          ? `R$${localeString(discounted_price, 2)} / R$ ${localeString(price, 2)}`
          : '-',
        percentage: `${percentage_discount ? percentage_discount + '%' : '-'}`,
        takepay: !take || !pay ? '-' : `${take} / ${pay}`,
        combo: `${target_products?.percentage_discount ? target_products.percentage_discount + '%' : '-'}`,
        cashback_percentage: '-',
        cashback_absolute: '-',
        points: '-',
      }

      return discounts[type]
    },
    getUnits(item) {
      if (!item.app_units || !item.app_units.length) return '-'
      else if (item.app_units.length >= 2) return `${item.app_units.length} lojas`
      return item.app_units[0].name
    },
  },
}
</script>
<style lang="scss" scoped>
.img-container {
  width: 35px;
  height: 35px;

  & img {
    display: block;
    max-width: 100%;
    max-height: 100%;
  }
}
</style>
